import { useCallback } from 'react'
import { Address } from 'viem'
import { useAccount } from 'wagmi'

import abiStakingNftUsdc from 'abi/abi-staking-nft-usdc.json'
import { ContractStatusEnum } from 'constants/index'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import { useContractManager } from 'hooks/use-contract-manager'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { parserPercentContract } from 'utils/contract'
import { logInfo } from 'utils/logger'

interface Params {
  fields: {
    aprBaseBP: number
    startDate: number
    endDate: number
    isActive: boolean
    rewardBase: number
    duration: number
    aprBonusTiers: { tierId: string; aprBonusBP: number }[]
  }
  ptoId: string
  isUpdate?: boolean
  collectionAddress: string
}

export const useStakeNFTUsdcReward = (contractAddress: Address | null) => {
  const { address } = useAccount()
  const contract = useContractViem(contractAddress, abiStakingNftUsdc)
  const { isContractManager } = useContractManager({ contract })

  const prepareUsdcParams = ({ fields, collectionAddress, isUpdate = false }: Params) => {
    const formattedRewardBonusTiers = fields.aprBonusTiers.map((t: any) => ({
      tierId: BigInt(t.tierId),
      aprBonusBP: parserPercentContract(t.aprBonusBP),
    }))

    return [
      collectionAddress,
      parserPercentContract(fields.aprBaseBP),
      isUpdate ? fields.isActive : undefined,
      formattedRewardBonusTiers,
    ].filter((v) => v != undefined)
  }

  const addNftToStakeUsdcPool = useCallback(
    async (params: Params) => {
      try {
        const contractParams = prepareUsdcParams(params)

        const { request } = await contract.simulate.addNFTCollection(contractParams, {
          account: address,
        })

        const hash = await clientWallet.writeContract(request)

        const receipt = await publicWallet.waitForTransactionReceipt({
          hash,
          confirmations: 1,
        })

        if (receipt.status === ContractStatusEnum.SUCCESS) {
          logInfo('Deployed pool address')
          return receipt
        }

        return null
      } catch (error) {
        console.error('Error adding NFT to stake pool:', error)
        throw error
      }
    },
    [contract, address]
  )

  const updateNftToStakeUsdcPool = useCallback(
    async (params: Params) => {
      try {
        const contractParams = prepareUsdcParams({ ...params, isUpdate: true })

        const { request } = await contract.simulate.updateNFTCollection(contractParams, {
          account: address,
        })

        const hash = await clientWallet.writeContract(request)

        const receipt = await publicWallet.waitForTransactionReceipt({
          hash,
          confirmations: 1,
        })

        if (receipt.status === ContractStatusEnum.SUCCESS) {
          logInfo('Deployed pool address')
          return receipt
        }

        return null
      } catch (error) {
        console.error('Error edit NFT to stake pool:', error)
        throw error
      }
    },
    [contract, address]
  )

  return { isContractManager, addNftToStakeUsdcPool, updateNftToStakeUsdcPool }
}
